
import { Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const PricingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Simple, Transparent Pricing</h1>
          <p className="text-muted-foreground text-xl">
            Find the perfect charging solution for your needs
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Host Plan */}
          <Card className="border-2 hover:border-primary transition-colors">
            <CardHeader>
              <CardTitle>Host Plan</CardTitle>
              <CardDescription>For EV charger owners</CardDescription>
              <div className="mt-4">
                <span className="text-3xl font-bold">5%</span>
                <span className="text-muted-foreground ml-2">commission</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-4">
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Set your own rates</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>24/7 support</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Insurance coverage</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Flexible availability</span>
                </li>
              </ul>
              <Button 
                className="w-full mt-6" 
                onClick={() => navigate('/host-charger')}
              >
                Start Hosting
              </Button>
            </CardContent>
          </Card>

          {/* Standard Plan */}
          <Card className="border-2 border-primary">
            <CardHeader>
              <div className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm w-fit mb-4">
                Most Popular
              </div>
              <CardTitle>Standard Plan</CardTitle>
              <CardDescription>For regular EV drivers</CardDescription>
              <div className="mt-4">
                <span className="text-3xl font-bold">$0</span>
                <span className="text-muted-foreground ml-2">+ charging fees</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-4">
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Access all stations</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Real-time availability</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Mobile app access</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Pay-as-you-go</span>
                </li>
              </ul>
              <Button 
                className="w-full mt-6" 
                onClick={() => navigate('/login')}
              >
                Get Started
              </Button>
            </CardContent>
          </Card>

          {/* Premium Plan */}
          <Card className="border-2 hover:border-primary transition-colors">
            <CardHeader>
              <CardTitle>Premium Plan</CardTitle>
              <CardDescription>For frequent chargers</CardDescription>
              <div className="mt-4">
                <span className="text-3xl font-bold">$19.99</span>
                <span className="text-muted-foreground ml-2">/month</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-4">
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>All Standard features</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>10% charging discount</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Priority support</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-primary mr-2" />
                  <span>Advanced statistics</span>
                </li>
              </ul>
              <Button 
                className="w-full mt-6" 
                onClick={() => navigate('/login')}
              >
                Subscribe Now
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
          <div className="max-w-2xl mx-auto space-y-6 text-left">
            <div>
              <h3 className="font-semibold mb-2">How does the host commission work?</h3>
              <p className="text-muted-foreground">
                We take a 5% commission on each charging session to cover platform costs, insurance, and support services. You keep 95% of your earnings.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Are there any hidden fees?</h3>
              <p className="text-muted-foreground">
                No hidden fees! Standard users only pay for charging, and Premium users pay a flat monthly fee plus discounted charging rates.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Can I cancel my Premium subscription?</h3>
              <p className="text-muted-foreground">
                Yes, you can cancel your Premium subscription at any time. You'll continue to have access until the end of your billing period.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
